import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useUI } from "@/context/AppContext";
import { fieldCls, labelCls, errCls } from "@/components/formStyles";

const PRODUCTS = ["cacao", "fruits", "cajou", "cafe", "caoutchouc", "kola", "autre"];
const CERTS = ["globalgap", "fairtrade", "rainforest", "bio", "autre"];
const EUDR_PRODUCTS = ["cacao", "cafe", "caoutchouc"];

export default function BuyerForm() {
  const ui = useUI();
  const [form, setForm] = useState({
    full_name: "", company: "", country: "", email: "", phone: "",
    job_title: "",
    role: "", products: [], product_other: "", volume: "", message: "", website: "", math_answer: "",
    certifications: [], cert_other: "", cert_status: "", support_wanted: false, certificate_numbers: "",
    attestation: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [captcha, setCaptcha] = useState({ a: null, b: null, token: "" });
  const [debouncedOther, setDebouncedOther] = useState("");

  // debounce the free-text "Autre" product so the persuasive message
  // doesn't flicker/rewrite on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedOther(form.product_other), 500);
    return () => clearTimeout(t);
  }, [form.product_other]);

  const loadCaptcha = useCallback(async () => {
    try {
      const { data } = await api.get("/forms/captcha");
      setCaptcha(data);
    } catch { setCaptcha({ a: null, b: null, token: "" }); }
  }, []);
  useEffect(() => { loadCaptcha(); }, [loadCaptcha]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleProduct = (p) =>
    setForm((f) => ({ ...f, products: f.products.includes(p) ? f.products.filter((x) => x !== p) : [...f.products, p] }));

  // selecting a real certification clears the "en cours / non certifié" status
  const toggleCert = (c) =>
    setForm((f) => ({
      ...f,
      certifications: f.certifications.includes(c) ? f.certifications.filter((x) => x !== c) : [...f.certifications, c],
      cert_status: "",
      support_wanted: false,
    }));

  // selecting a status clears the real certifications
  const setCertStatus = (status) =>
    setForm((f) => ({
      ...f,
      cert_status: f.cert_status === status ? "" : status,
      certifications: [],
      cert_other: "",
      certificate_numbers: "",
      support_wanted: false,
    }));

  // valid free-text product: >= 2 chars and contains at least one letter
  const isValidOther = (v) => v.trim().length >= 2 && /\p{L}/u.test(v);
  const needsOther = form.products.includes("autre");
  const otherReady = !needsOther || isValidOther(form.product_other);

  const showCertSection = (form.role === "Fournisseur" || form.role === "Producteur") && otherReady;
  const hasRealCerts = form.certifications.length > 0;
  const hasStatus = form.cert_status === "en_cours" || form.cert_status === "non_certifie";

  const supportMessage = () => {
    const selected = form.products;
    if (selected.length === 0) return "";
    const names = selected
      .map((p) => (p === "autre" ? (debouncedOther.trim() || ui.prod_autre) : ui[`prod_${p}`]))
      .join(", ");
    // "autre" is treated as a non-EUDR product
    const isEudr = selected.some((p) => EUDR_PRODUCTS.includes(p));
    const template = isEudr ? ui.msg_eudr : ui.msg_standard;
    return template.replace("{products}", names);
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = ui.required;
    if (!form.job_title.trim()) e.job_title = ui.required;
    if (!form.company.trim()) e.company = ui.required;
    if (!form.country.trim()) e.country = ui.required;
    if (!form.email.trim()) e.email = ui.required;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = ui.invalid_email;
    if (!form.role) e.role = ui.select_one;
    if (form.products.length === 0) e.products = ui.pick_products;
    if (form.products.includes("autre")) {
      if (!form.product_other.trim()) e.product_other = ui.required;
      else if (!isValidOther(form.product_other)) e.product_other = ui.product_other_invalid;
    }
    if (!form.message.trim()) e.message = ui.required;
    if (captcha.a == null || parseInt(form.math_answer, 10) !== captcha.a + captcha.b) e.math_answer = ui.math_wrong;
    if (!form.attestation) e.attestation = ui.attestation_error;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        // strip certification data if section not visible for the selected role
        certifications: showCertSection ? form.certifications : [],
        cert_other: showCertSection ? form.cert_other : "",
        cert_status: showCertSection ? form.cert_status : "",
        support_wanted: showCertSection ? form.support_wanted : false,
        certificate_numbers: showCertSection ? form.certificate_numbers : "",
        captcha_token: captcha.token,
        captcha_answer: parseInt(form.math_answer, 10),
      };
      await api.post("/forms/buyer", payload);
      setDone(true);
      toast.success(ui.success);
    } catch (err) {
      toast.error(err?.response?.data?.detail || ui.error);
      loadCaptcha();
      setForm((f) => ({ ...f, math_answer: "" }));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-[#8CC63F]/30 bg-white p-10 text-center" data-testid="buyer-form-success">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r from-[#8CC63F] to-[#4E7D2C] text-2xl text-white">✓</div>
        <p className="font-montserrat text-xl font-bold text-neutral-900">{ui.success}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="buyer-form" noValidate>
      {/* honeypot */}
      <input
        type="text" tabIndex={-1} autoComplete="off"
        value={form.website} onChange={(e) => set("website", e.target.value)}
        className="absolute -left-[9999px] h-0 w-0 opacity-0" aria-hidden="true" data-testid="buyer-honeypot"
      />

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelCls}>{ui.full_name} *</label>
          <input className={fieldCls} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="buyer-full-name" />
          {errors.full_name && <p className={errCls}>{errors.full_name}</p>}
        </div>
        <div>
          <label className={labelCls}>{ui.job_title} *</label>
          <input className={fieldCls} value={form.job_title} onChange={(e) => set("job_title", e.target.value)} data-testid="buyer-job-title" />
          {errors.job_title && <p className={errCls}>{errors.job_title}</p>}
        </div>
        <div>
          <label className={labelCls}>{ui.company} *</label>
          <input className={fieldCls} value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="buyer-company" />
          {errors.company && <p className={errCls}>{errors.company}</p>}
        </div>
        <div>
          <label className={labelCls}>{ui.country} *</label>
          <input className={fieldCls} value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="buyer-country" />
          {errors.country && <p className={errCls}>{errors.country}</p>}
        </div>
        <div>
          <label className={labelCls}>{ui.email} *</label>
          <input type="email" className={fieldCls} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="buyer-email" />
          {errors.email && <p className={errCls}>{errors.email}</p>}
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>{ui.phone}</label>
          <input className={fieldCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="buyer-phone" />
        </div>
      </div>

      <div>
        <label className={labelCls}>{ui.i_am} *</label>
        <div className="mt-2 flex flex-wrap gap-3">
          {[["Acheteur", ui.role_buyer], ["Fournisseur", ui.role_supplier], ["Producteur", ui.role_producer]].map(([val, lbl]) => (
            <label key={val} className={`cursor-pointer rounded-full border px-4 py-2 font-poppins text-sm transition-colors ${form.role === val ? "border-[#4E7D2C] bg-[#8CC63F]/10 text-[#4E7D2C]" : "border-black/10 text-neutral-600 hover:border-[#8CC63F]"}`}>
              <input type="radio" name="role" className="sr-only" checked={form.role === val} onChange={() => set("role", val)} data-testid={`buyer-role-${val}`} />
              {lbl}
            </label>
          ))}
        </div>
        {errors.role && <p className={errCls}>{errors.role}</p>}
      </div>

      <div>
        <label className={labelCls}>{ui.products} *</label>
        <div className="mt-2 flex flex-wrap gap-3">
          {PRODUCTS.map((p) => (
            <label key={p} className={`cursor-pointer rounded-full border px-4 py-2 font-poppins text-sm transition-colors ${form.products.includes(p) ? "border-[#F5821F] bg-[#F5821F]/10 text-[#F5821F]" : "border-black/10 text-neutral-600 hover:border-[#F5821F]"}`}>
              <input type="checkbox" className="sr-only" checked={form.products.includes(p)} onChange={() => toggleProduct(p)} data-testid={`buyer-product-${p}`} />
              {p === "autre" ? ui.prod_autre : ui[`prod_${p}`]}
            </label>
          ))}
        </div>
        {form.products.includes("autre") && (
          <input
            className={`${fieldCls} mt-3`} placeholder={ui.product_other_ph}
            value={form.product_other} onChange={(e) => set("product_other", e.target.value)}
            onBlur={() => setDebouncedOther(form.product_other)}
            data-testid="buyer-product-other-text"
          />
        )}
        {errors.products && <p className={errCls}>{errors.products}</p>}
        {errors.product_other && <p className={errCls}>{errors.product_other}</p>}
        {needsOther && !otherReady && (form.role === "Fournisseur" || form.role === "Producteur") && (
          <p className="mt-2 font-poppins text-xs text-neutral-500" data-testid="buyer-other-hint">{ui.product_other_hint}</p>
        )}
      </div>

      {showCertSection && (
        <div className="rounded-2xl border border-[#8CC63F]/30 bg-[#8CC63F]/5 p-5" data-testid="buyer-cert-section">
          <p className="font-montserrat text-sm font-bold uppercase tracking-wide text-[#4E7D2C]">{ui.cert_section_title}</p>

          <div className="mt-4">
            <label className={labelCls}>{ui.cert_obtained_label}</label>
            <div className="mt-2 flex flex-wrap gap-3">
              {CERTS.map((c) => {
                const disabled = hasStatus;
                return (
                  <label
                    key={c}
                    className={`rounded-full border px-4 py-2 font-poppins text-sm transition-colors ${disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"} ${form.certifications.includes(c) ? "border-[#4E7D2C] bg-[#8CC63F]/10 text-[#4E7D2C]" : "border-black/10 text-neutral-600 hover:border-[#8CC63F]"}`}
                  >
                    <input
                      type="checkbox" className="sr-only" disabled={disabled}
                      checked={form.certifications.includes(c)} onChange={() => toggleCert(c)}
                      data-testid={`buyer-cert-${c}`}
                    />
                    {ui[`cert_${c}`]}
                  </label>
                );
              })}
            </div>
            {form.certifications.includes("autre") && (
              <input
                className={`${fieldCls} mt-3`} placeholder={ui.cert_other_ph}
                value={form.cert_other} onChange={(e) => set("cert_other", e.target.value)}
                data-testid="buyer-cert-other-text"
              />
            )}
          </div>

          {hasRealCerts && (
            <div className="mt-4">
              <label className={labelCls}>{ui.cert_numbers_label}</label>
              <input
                className={fieldCls} value={form.certificate_numbers}
                onChange={(e) => set("certificate_numbers", e.target.value)}
                data-testid="buyer-cert-numbers"
              />
            </div>
          )}

          <div className="mt-4">
            <label className={labelCls}>{ui.cert_status_label}</label>
            <div className="mt-2 flex flex-wrap gap-3">
              {[["en_cours", ui.cert_in_progress], ["non_certifie", ui.cert_none]].map(([val, lbl]) => (
                <label
                  key={val}
                  className={`cursor-pointer rounded-full border px-4 py-2 font-poppins text-sm transition-colors ${form.cert_status === val ? "border-[#F5821F] bg-[#F5821F]/10 text-[#F5821F]" : "border-black/10 text-neutral-600 hover:border-[#F5821F]"}`}
                >
                  <input
                    type="checkbox" className="sr-only"
                    checked={form.cert_status === val} onChange={() => setCertStatus(val)}
                    data-testid={`buyer-cert-status-${val}`}
                  />
                  {lbl}
                </label>
              ))}
            </div>
          </div>

          {hasStatus && (
            <div className="mt-4">
              {supportMessage() && (
                <div className="rounded-xl border border-[#F5821F]/30 bg-[#F5821F]/5 p-4 font-poppins text-sm leading-relaxed text-neutral-700" data-testid="buyer-support-message">
                  {supportMessage()}
                </div>
              )}
              <label className="mt-3 flex cursor-pointer items-start gap-3 font-poppins text-sm text-neutral-700">
                <input
                  type="checkbox" className="mt-0.5 h-4 w-4 accent-[#4E7D2C]"
                  checked={form.support_wanted} onChange={(e) => set("support_wanted", e.target.checked)}
                  data-testid="buyer-support-wanted"
                />
                {ui.support_label}
              </label>
            </div>
          )}
        </div>
      )}

      <div>
        <label className={labelCls}>{ui.volume}</label>
        <input className={fieldCls} value={form.volume} onChange={(e) => set("volume", e.target.value)} data-testid="buyer-volume" />
      </div>

      <div>
        <label className={labelCls}>{ui.message} *</label>
        <textarea rows={4} className={fieldCls} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="buyer-message" />
        {errors.message && <p className={errCls}>{errors.message}</p>}
      </div>

      <div>
        <label className={labelCls}>{ui.security_q} : {captcha.a != null ? `${captcha.a} + ${captcha.b}` : "…"} = ? *</label>
        <input type="number" className={`${fieldCls} max-w-[140px]`} value={form.math_answer} onChange={(e) => set("math_answer", e.target.value)} data-testid="buyer-math" />
        {errors.math_answer && <p className={errCls}>{errors.math_answer}</p>}
      </div>

      <div>
        <label className="flex cursor-pointer items-start gap-3 font-poppins text-sm text-neutral-700" data-testid="buyer-attestation-label">
          <input
            type="checkbox" className="mt-0.5 h-4 w-4 shrink-0 accent-[#4E7D2C]"
            checked={form.attestation} onChange={(e) => set("attestation", e.target.checked)}
            data-testid="buyer-attestation"
          />
          <span>{ui.attestation} *</span>
        </label>
        {errors.attestation && <p className={errCls}>{errors.attestation}</p>}
      </div>

      <button
        type="submit" disabled={submitting} data-testid="buyer-submit"
        className="w-full rounded-full bg-gradient-to-r from-[#8CC63F] to-[#4E7D2C] px-8 py-4 font-poppins text-base font-semibold text-white shadow-md transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60 sm:w-auto"
      >
        {submitting ? ui.sending : ui.send_request}
      </button>
    </form>
  );
}
